import React, { useEffect, useState, useCallback } from 'react';
import {
  Sparkles,
  Plus,
  Trash2,
  GitBranch,
  Video,
  FileText,
  Link2,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Eye,
  Globe,
} from 'lucide-react';
import { showcaseService, ShowcaseEntry, ShowcaseLink, ShowcaseLinkType } from '../../services/showcase';
import { ConfirmationModal } from '../ConfirmationModal';

interface Props {
  sessionId: string;
  showcaseEnabled?: boolean;
  showcaseId?: string;
  showcaseLinks?: ShowcaseLink[];
}

const LINK_TYPES: { value: ShowcaseLinkType; label: string; icon: React.ElementType }[] = [
  { value: 'repo', label: 'Repository', icon: GitBranch },
  { value: 'video', label: 'Video', icon: Video },
  { value: 'doc', label: 'Document', icon: FileText },
  { value: 'other', label: 'Other', icon: Link2 },
];

function StatusBadge({ status }: { status: string }) {
  if (status === 'generating') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-600 border border-amber-200">
        <RefreshCw size={11} className="animate-spin" /> Generating…
      </span>
    );
  }
  if (status === 'draft') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-600 border border-blue-200">
        <Eye size={11} /> Draft — review before publishing
      </span>
    );
  }
  if (status === 'published') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600 border border-green-200">
        <CheckCircle size={11} /> Published to showcase
      </span>
    );
  }
  return null;
}

export default function ShowcaseTab({ sessionId, showcaseEnabled, showcaseId: initialShowcaseId, showcaseLinks: initialLinks }: Props) {
  const [enabled, setEnabled] = useState(showcaseEnabled || false);
  const [showcaseId, setShowcaseId] = useState(initialShowcaseId);
  const [entry, setEntry] = useState<ShowcaseEntry | null>(null);
  const [links, setLinks] = useState<ShowcaseLink[]>(initialLinks || []);
  const [newLink, setNewLink] = useState<Partial<ShowcaseLink>>({ type: 'repo', label: '', url: '' });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollInterval, setPollInterval] = useState<ReturnType<typeof setInterval> | null>(null);
  // Stable React keys for the free-text "learnings" list, decoupled from array position.
  // Reset (fresh ids per item) whenever a full entry arrives from the server;
  // kept in sync with entry.learnings by the add/edit/remove handlers below.
  const [learningIds, setLearningIds] = useState<string[]>([]);
  const [confirmationModal, setConfirmationModal] = useState<{ isOpen: boolean; action: 'disable' | 'publish' }>({ isOpen: false, action: 'disable' });

  const setEntryFromServer = (e: ShowcaseEntry) => {
    setEntry(e);
    setLearningIds((e.learnings || []).map(() => crypto.randomUUID()));
  };

  const loadEntry = useCallback(async (id: string) => {
    try {
      const e = await showcaseService.get(id);
      setEntryFromServer(e);
      if (e.status !== 'generating' && pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    } catch { /* non-fatal */ }
  }, [pollInterval]);

  useEffect(() => {
    if (!showcaseId) return;
    (async () => {
      const e = await showcaseService.get(showcaseId).catch(() => null);
      if (!e) return;
      setEntryFromServer(e);
      if (e.status === 'generating') startPolling(showcaseId);
    })();
  }, [showcaseId]);

  useEffect(() => {
    return () => { if (pollInterval) clearInterval(pollInterval); };
  }, [pollInterval]);

  const startPolling = (id: string) => {
    if (pollInterval) clearInterval(pollInterval);
    const interval = setInterval(() => loadEntry(id), 5000);
    setPollInterval(interval);
  };

  const handleEnable = async () => {
    setSaving(true);
    setError(null);
    try {
      const created = await showcaseService.enable(sessionId, links);
      setEnabled(true);
      setShowcaseId(created.showcaseId);
      setEntryFromServer(created);
      if (created.status === 'generating') startPolling(created.showcaseId);
    } catch (e: any) {
      setError(e.message || 'Failed to enable showcase');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = () => {
    setConfirmationModal({ isOpen: true, action: 'disable' });
  };

  const handleConfirmDisable = async () => {
    setSaving(true);
    setError(null);
    try {
      await showcaseService.disable(sessionId);
      setEnabled(false);
      setShowcaseId(undefined);
      setEntry(null);
      setLearningIds([]);
    } catch (e: any) {
      setError(e.message || 'Failed to disable showcase');
    } finally {
      setSaving(false);
      setConfirmationModal({ isOpen: false, action: 'disable' });
    }
  };

  const handleAddLink = async () => {
    if (!newLink.url || !newLink.label) return;
    const updated = [...links, newLink as ShowcaseLink];
    setLinks(updated);
    setNewLink({ type: 'repo', label: '', url: '' });
    if (showcaseId) {
      await showcaseService.updateLinks(sessionId, updated).catch(() => {});
    }
  };

  const handleRemoveLink = async (i: number) => {
    const updated = links.filter((_, idx) => idx !== i);
    setLinks(updated);
    if (showcaseId) {
      await showcaseService.updateLinks(sessionId, updated).catch(() => {});
    }
  };

  const handleGenerate = async () => {
    if (!showcaseId) return;
    setGenerating(true);
    setError(null);
    try {
      await showcaseService.generate(sessionId);
      setEntry(prev => prev ? { ...prev, status: 'generating' } : prev);
      startPolling(showcaseId);
    } catch (e: any) {
      setError(e.message || 'Failed to start generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleFieldChange = (field: keyof ShowcaseEntry, value: any) => {
    setEntry(prev => prev ? { ...prev, [field]: value } : prev);
  };

  const handleSaveDraft = async () => {
    if (!entry || !showcaseId) return;
    setSaving(true);
    try {
      const updated = await showcaseService.update(showcaseId, {
        challengeTitle: entry.challengeTitle,
        challengeSummary: entry.challengeSummary,
        industry: entry.industry,
        tags: entry.tags,
        learnings: entry.learnings,
      });
      setEntryFromServer(updated);
    } catch (e: any) {
      setError(e.message || 'Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = () => {
    if (!showcaseId) return;
    setConfirmationModal({ isOpen: true, action: 'publish' });
  };

  const handleConfirmPublish = async () => {
    if (!showcaseId) return;
    setPublishing(true);
    setError(null);
    try {
      await handleSaveDraft();
      const published = await showcaseService.publish(showcaseId);
      setEntryFromServer(published);
    } catch (e: any) {
      setError(e.message || 'Failed to publish');
    } finally {
      setPublishing(false);
      setConfirmationModal({ isOpen: false, action: 'publish' });
    }
  };

  const INDUSTRIES = [
    'Financial Services', 'Public Sector', 'Healthcare', 'Retail',
    'Logistics', 'Manufacturing', 'Technology', 'Energy', 'Media', 'Professional Services',
  ];

  const learnings = entry?.learnings || [];
  const learningRows = learnings.map((text, i) => ({ text, i, id: learningIds[i] ?? `learning-fallback-${i}` }));

  return (
    <div className="flex flex-col gap-5 p-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Sparkles size={16} />
          </div>
          <div>
            <h2 className="text-base font-bold text-navy">Engagement Showcase</h2>
            <p className="text-xs text-slate-500">Add this session to the anonymised platform showcase</p>
          </div>
        </div>
        {entry && <StatusBadge status={entry.status} />}
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
          <AlertCircle size={15} className="flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Opt-in toggle */}
      {!enabled ? (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-navy">Add to showcase</p>
            <p className="text-xs text-slate-500 mt-0.5">Enable to allow an anonymised summary to be generated and published.</p>
          </div>
          <button
            onClick={handleEnable}
            disabled={saving}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            <Sparkles size={14} /> Enable
          </button>
        </div>
      ) : (
        <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-navy">Showcase enabled</p>
            <p className="text-xs text-slate-500 mt-0.5">This session is opted in to the showcase.</p>
          </div>
          <button
            onClick={handleDisable}
            disabled={saving}
            className="flex items-center gap-2 rounded-xl h-9 px-4 border border-red-200 bg-white text-red-600 text-sm font-semibold hover:bg-red-50 transition-colors disabled:opacity-60"
          >
            <Trash2 size={13} /> Remove
          </button>
        </div>
      )}

      {enabled && (
        <>
          {/* Asset links */}
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0]">
              <p className="text-[11px] font-bold text-primary tracking-widest uppercase">Shareable Assets</p>
              <p className="text-xs text-slate-500 mt-0.5">Links to repos, videos, or docs to appear on the showcase card.</p>
            </div>
            <div className="p-5 flex flex-col gap-3">
              {links.map((link, i) => {
                const Icon = LINK_TYPES.find(t => t.value === link.type)?.icon || Link2;
                return (
                  <div key={link.url} className="flex items-center gap-3 p-3 rounded-xl border border-[#e2e8f0] bg-slate-50/50">
                    <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400 flex-shrink-0">
                      <Icon size={13} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-navy truncate">{link.label}</p>
                      <p className="text-xs text-slate-400 truncate">{link.url}</p>
                    </div>
                    <button onClick={() => handleRemoveLink(i)} className="text-slate-400 hover:text-red-500 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                );
              })}

              {/* Add link form */}
              <div className="flex gap-2 items-end mt-1">
                <select
                  value={newLink.type}
                  onChange={e => setNewLink(p => ({ ...p, type: e.target.value as ShowcaseLinkType }))}
                  className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white w-36 flex-shrink-0"
                >
                  {LINK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <input
                  type="text"
                  placeholder="Label"
                  value={newLink.label}
                  onChange={e => setNewLink(p => ({ ...p, label: e.target.value }))}
                  className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <input
                  type="url"
                  placeholder="URL"
                  value={newLink.url}
                  onChange={e => setNewLink(p => ({ ...p, url: e.target.value }))}
                  className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  onClick={handleAddLink}
                  disabled={!newLink.url || !newLink.label}
                  className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-slate-100 hover:bg-slate-200 text-slate-600 text-sm font-semibold transition-colors disabled:opacity-40"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
            </div>
          </div>

          {/* AI generation */}
          <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold text-primary tracking-widest uppercase">AI Draft</p>
                <p className="text-xs text-slate-500 mt-0.5">Generate an anonymised catalog entry from session content.</p>
              </div>
              <button
                onClick={handleGenerate}
                disabled={generating || entry?.status === 'generating'}
                className="flex items-center gap-2 rounded-xl h-9 px-4 border border-[#e2e8f0] bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60"
              >
                {entry?.status === 'generating' ? (
                  <><RefreshCw size={13} className="animate-spin" /> Generating…</>
                ) : (
                  <><Sparkles size={13} /> {entry ? 'Regenerate' : 'Generate draft'}</>
                )}
              </button>
            </div>

            {entry && entry.status !== 'generating' && (
              <div className="p-5 flex flex-col gap-4">
                {/* Challenge title */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Challenge title</label>
                  <input
                    type="text"
                    value={entry.challengeTitle || ''}
                    onChange={e => handleFieldChange('challengeTitle', e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    placeholder="Concise title, no client identifiers"
                  />
                </div>

                {/* Industry */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Industry</label>
                  <select
                    value={entry.industry || ''}
                    onChange={e => handleFieldChange('industry', e.target.value)}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                  >
                    <option value="">Select industry…</option>
                    {INDUSTRIES.map(industry => <option key={industry}>{industry}</option>)}
                  </select>
                </div>

                {/* Summary */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Challenge summary</label>
                  <textarea
                    value={entry.challengeSummary || ''}
                    onChange={e => handleFieldChange('challengeSummary', e.target.value)}
                    rows={5}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                    placeholder="150–200 word anonymised description"
                  />
                  <p className="mt-1 text-xs text-slate-400 text-right">{(entry.challengeSummary || '').length} chars</p>
                </div>

                {/* Tags */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Tags</label>
                  <input
                    type="text"
                    value={(entry.tags || []).join(', ')}
                    onChange={e => handleFieldChange('tags', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                    className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    placeholder="Comma-separated tags (e.g. AI Strategy, Service Design)"
                  />
                </div>

                {/* Learnings */}
                <div>
                  <label className="block text-sm font-semibold text-navy mb-1.5">Key learnings</label>
                  {learningRows.map(({ text, i, id }) => (
                    <div key={id} className="flex gap-2 mb-2">
                      <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-2.5">{i + 1}</span>
                      <input
                        type="text"
                        value={text}
                        onChange={e => {
                          const updated = [...(entry.learnings || [])];
                          updated[i] = e.target.value;
                          handleFieldChange('learnings', updated);
                        }}
                        className="flex-1 border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                      <button
                        onClick={() => {
                          handleFieldChange('learnings', (entry.learnings || []).filter((_, idx) => idx !== i));
                          setLearningIds(prev => prev.filter((_, idx) => idx !== i));
                        }}
                        className="text-slate-400 hover:text-red-500 transition-colors mt-2"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      handleFieldChange('learnings', [...(entry.learnings || []), '']);
                      setLearningIds(prev => [...prev, crypto.randomUUID()]);
                    }}
                    className="text-xs text-primary hover:text-primary-dark transition-colors flex items-center gap-1 mt-1"
                  >
                    <Plus size={12} /> Add learning
                  </button>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t border-[#e2e8f0]">
                  <button
                    onClick={handleSaveDraft}
                    disabled={saving}
                    className="flex items-center gap-2 rounded-xl h-9 px-4 border border-[#e2e8f0] bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 transition-colors disabled:opacity-60"
                  >
                    Save draft
                  </button>
                  {entry.status !== 'published' && (
                    <button
                      onClick={handlePublish}
                      disabled={publishing || saving}
                      className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
                    >
                      <Globe size={13} /> Publish to showcase
                    </button>
                  )}
                </div>
              </div>
            )}

            {entry?.status === 'generating' && (
              <div className="p-8 text-center text-slate-400 text-sm">
                <RefreshCw size={20} className="animate-spin mx-auto mb-3 text-primary" />
                AI is generating an anonymised draft… this takes about 30 seconds.
              </div>
            )}

            {!entry && (
              <div className="p-8 text-center text-slate-400 text-sm">
                Click "Generate draft" to create an anonymised catalog entry using session content.
              </div>
            )}
          </div>
        </>
      )}

      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.action === 'disable' ? 'Remove from showcase?' : 'Publish to showcase?'}
        message={confirmationModal.action === 'disable'
          ? 'Any draft or published entry will be deleted.'
          : 'This entry will be visible to all authenticated users.'
        }
        confirmLabel={confirmationModal.action === 'disable' ? 'Remove' : 'Publish'}
        onConfirm={confirmationModal.action === 'disable' ? handleConfirmDisable : handleConfirmPublish}
        onCancel={() => setConfirmationModal({ isOpen: false, action: confirmationModal.action })}
        isDestructive={confirmationModal.action === 'disable'}
        isLoading={saving || publishing}
      />
    </div>
  );
}
